import { useEffect, useState } from 'react';
import { useTheme as useNextTheme } from 'next-themes';
import { useAuth } from './useAuth';
import { getApiPath } from '@/lib/api';
import { fetchWithAuth } from '@/lib/fetchWithAuth';

type ThemePreference = 'light' | 'dark' | 'system';

/**
 * Hook customizado que integra next-themes com o backend
 * Carrega a preferência do usuário do banco de dados e salva quando alterada
 */
export function useThemeWithBackend() {
  const { theme, setTheme, resolvedTheme, systemTheme } = useNextTheme();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Carregar preferência do backend quando o usuário estiver logado
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    const loadThemePreference = async () => {
      try {
        const response = await fetchWithAuth(getApiPath('/users/theme-preference'), {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.theme_preference && data.theme_preference !== theme) {
            // Usar setTheme do next-themes diretamente para evitar loop
            setTheme(data.theme_preference as ThemePreference);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar preferência de tema:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadThemePreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Salvar preferência no backend (chamada manual)
  const saveThemePreference = async (newTheme: ThemePreference) => {
    if (!user?.id) {
      // Se não estiver logado, apenas atualizar localmente
      setTheme(newTheme);
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(getApiPath('/users/theme-preference'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ theme_preference: newTheme }),
      });

      if (!response.ok) {
        // Tentar ler o erro, mas não falhar se não conseguir
        let errorMessage = 'Erro ao salvar preferência de tema';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // Se não conseguir parsear JSON, usar mensagem padrão
        }
        
        // Log apenas em desenvolvimento para não poluir console
        if (import.meta.env.DEV) {
          console.warn('⚠️ Erro ao salvar preferência de tema:', errorMessage);
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setTheme(newTheme);
      
      if (import.meta.env.DEV) {
        console.log('✅ Preferência de tema salva:', data);
      }
      
      return data;
    } catch (error) {
      // Log apenas em desenvolvimento
      if (import.meta.env.DEV) {
        console.warn('⚠️ Erro ao salvar preferência de tema:', error);
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Função para atualizar tema apenas localmente (sem salvar no backend)
  const setThemeLocal = (newTheme: ThemePreference) => {
    setTheme(newTheme);
  };

  return {
    theme: theme as ThemePreference | undefined,
    setTheme: setThemeLocal, // Atualiza apenas localmente
    saveTheme: saveThemePreference, // Salva no backend
    resolvedTheme,
    systemTheme,
    isLoading,
    isSaving,
  };
}
